import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Home, LogOut, Calendar, Clock, Sun, Bell, Store, UserPlus, FileText, Loader2, ClipboardList } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from './LanguageToggle';
import axios from 'axios';

// Logo
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Notification Item Component
const NotificationItem = ({ icon: Icon, color, title, count, onClick }) => (
  <motion.button
    whileHover={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
    onClick={onClick}
    className="w-full px-4 py-3 flex items-center gap-3 text-start transition-colors"
  >
    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
      <Icon className="h-4 w-4" style={{ color }} />
    </div>
    <div className="flex-1">
      <p className="text-[#F5F5F5] text-sm">{title}</p>
    </div>
    <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: `${color}15`, color }}>
      {count}
    </span>
  </motion.button>
);

export default function DashboardHeader({ 
  backPath,
  onBack,
  showBackButton = true
}) {
  const navigate = useNavigate();
  const { logout, getAuthHeaders } = useAuth();
  const { language } = useLanguage();
  
  // Notifications State
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  const [notifications, setNotifications] = useState({
    pendingStores: [],
    agentRequests: [],
    serviceRequests: [],
    myTasks: [],
    loading: true
  });
  
  // Time and Weather States
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weather, setWeather] = useState({ temp: '31°C', condition: 'sunny', loaded: true });

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weather data
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const lat = 25.2048;
        const lon = 55.2708;
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`
        );
        const data = await response.json();
        if (data.current_weather) {
          setWeather({
            temp: `${Math.round(data.current_weather.temperature)}°C`,
            condition: data.current_weather.weathercode < 3 ? 'sunny' : 'cloudy'
          });
        }
      } catch (error) {
        console.log('Weather fetch error:', error);
      }
    };
    fetchWeather();
    const weatherInterval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(weatherInterval);
  }, []);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const headers = getAuthHeaders ? getAuthHeaders() : {};
        const [storesRes, agentRes, serviceRes, myTasksRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/stores?platform=cloudgm_business`, { headers }).catch(() => ({ data: { stores: [] } })),
          axios.get(`${API_URL}/api/admin/agent-requests?status=pending`, { headers }).catch(() => ({ data: { data: [] } })),
          axios.get(`${API_URL}/api/admin/service-requests?status=pending`, { headers }).catch(() => ({ data: { requests: [] } })),
          axios.get(`${API_URL}/api/admin/assigned-tasks/mine`, { headers }).catch(() => ({ data: { data: [] } }))
        ]);
        
        setNotifications({
          pendingStores: (storesRes.data?.stores || []).filter(s => s.status === 'pending'),
          agentRequests: (agentRes.data?.data || agentRes.data?.requests || []).filter(r => (r.status || 'pending') === 'pending'),
          serviceRequests: serviceRes.data?.requests || [],
          myTasks: (myTasksRes.data?.data || []).filter(t => t.status === 'pending' || t.status === 'in_progress'),
          loading: false
        });
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setNotifications(prev => ({ ...prev, loading: false }));
      }
    };
    
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [getAuthHeaders]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  const totalNotifications = notifications.pendingStores.length + notifications.agentRequests.length + notifications.serviceRequests.length + notifications.myTasks.length;

  return (
    <header className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-2xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="h-20 flex items-center justify-between">
          {/* Right Side - Logo */}
          <div className="flex items-center gap-4">
            {showBackButton && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleBack}
                className="w-11 h-11 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
                data-testid="back-btn"
              >
                {language === 'ar'
                  ? <ArrowRight className="h-5 w-5 text-[#d4af37]" />
                  : <ArrowLeft  className="h-5 w-5 text-[#d4af37]" />}
              </motion.button>
            )}
            
            {/* Logo - clickable, navigates to dashboard */}
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-4 group cursor-pointer"
              data-testid="header-logo-btn"
              title={language === 'ar' ? 'الصفحة الرئيسية للوحة التحكم' : 'Main dashboard page'}
            >
              <div className="w-14 h-14 flex items-center justify-center">
                <img 
                  src={LOGO_TRIANGLE}
                  alt="ASB606"
                  className="w-12 h-12 object-contain drop-shadow-[0_0_20px_rgba(212,175,55,0.4)] group-hover:drop-shadow-[0_0_25px_rgba(212,175,55,0.6)] transition-all"
                />
              </div>
              <div className="text-start">
                <h1 className="text-xl font-bold bg-gradient-to-r from-[#d4af37] via-[#f3e5ab] to-[#c9a227] text-transparent bg-clip-text">
                  {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
                </h1>
                <p className="text-xs text-[#71717A] tracking-wider">ASB606 PLATFORMS GROUP</p>
              </div>
            </button>
          </div>
          
          {/* Center - Date, Time & Weather Cards - widened with refined spacing */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Date Card */}
            <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 hover:bg-[#d4af37]/5 hover:border-[#d4af37]/30 transition-all cursor-default min-w-[150px]">
              <div className={`flex flex-col flex-1 ${language === 'ar' ? 'items-end' : 'items-start'}`}>
                <p className="text-white text-sm font-semibold">
                  {currentTime.toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-GB', { weekday: 'long' })}
                </p>
                <p className="text-[#A1A1AA] text-[11px]">
                  {currentTime.toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                <Calendar className="h-4 w-4 text-emerald-400" />
              </div>
            </div>

            {/* Time Card */}
            <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 hover:bg-[#d4af37]/5 hover:border-[#d4af37]/30 transition-all cursor-default min-w-[140px]">
              <div className={`flex flex-col flex-1 ${language === 'ar' ? 'items-end' : 'items-start'}`}>
                <p className="text-white text-sm font-semibold tabular-nums">
                  {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </p>
                <p className="text-[#A1A1AA] text-[11px]">{language === 'ar' ? 'التوقيت المحلي' : 'Local time'}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                <Clock className="h-4 w-4 text-amber-400" />
              </div>
            </div>

            {/* Weather Card */}
            <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 hover:bg-[#d4af37]/5 hover:border-[#d4af37]/30 transition-all cursor-default min-w-[110px]">
              <div className={`flex flex-col flex-1 ${language === 'ar' ? 'items-end' : 'items-start'}`}>
                <p className="text-white text-sm font-semibold">{weather.temp}</p>
                <p className="text-[#A1A1AA] text-[11px]">{language === 'ar' ? 'دبي' : 'Dubai'}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-yellow-500/10 flex items-center justify-center border border-yellow-500/20">
                <Sun className="h-4 w-4 text-yellow-400" />
              </div>
            </div>
          </div>

          {/* Left Side - Actions */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative w-11 h-11 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
                data-testid="notifications-btn"
              >
                <Bell className="h-5 w-5 text-[#A1A1AA]" />
                {totalNotifications > 0 && (
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -left-1 w-5 h-5 bg-[#d4af37] text-black text-xs rounded-full flex items-center justify-center font-bold shadow-[0_0_10px_rgba(212,175,55,0.5)]"
                  >
                    {totalNotifications > 9 ? '9+' : totalNotifications}
                  </motion.span>
                )}
              </motion.button>
              
              {/* Notifications Dropdown */}
              <AnimatePresence>
                {showNotifications && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute top-14 start-0 w-80 bg-[#0A0A0A]/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                      <h3 className="text-[#F5F5F5] font-bold text-sm">
                        {language === 'ar' ? 'التنبيهات' : 'Notifications'}
                      </h3>
                      {totalNotifications > 0 && (
                        <span className="text-xs text-[#d4af37]">{totalNotifications}</span>
                      )}
                    </div>
                    
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.loading ? (
                        <div className="p-6 text-center">
                          <Loader2 className="h-6 w-6 animate-spin text-[#d4af37] mx-auto" />
                        </div>
                      ) : totalNotifications === 0 ? (
                        <div className="p-8 text-center">
                          <Bell className="h-10 w-10 mx-auto mb-3 text-[#71717A] opacity-50" />
                          <p className="text-[#71717A] text-sm">{language === 'ar' ? 'لا توجد تنبيهات' : 'No notifications'}</p>
                        </div>
                      ) : (
                        <>
                          {notifications.myTasks.length > 0 && (
                            <NotificationItem
                              icon={ClipboardList}
                              color="#8B5CF6"
                              title={language === 'ar' ? 'قائمة مهامي' : 'My tasks'}
                              count={notifications.myTasks.length}
                              onClick={() => { navigate('/dashboard/cloudgm/assigned-tasks?tab=mine'); setShowNotifications(false); }}
                            />
                          )}
                          {notifications.pendingStores.length > 0 && (
                            <NotificationItem
                              icon={Store}
                              color="#d4af37"
                              title={language === 'ar' ? 'طلبات تفعيل المتاجر' : 'Store Activation'}
                              count={notifications.pendingStores.length}
                              onClick={() => { navigate('/manage/cloudgm-business?tab=store-requests'); setShowNotifications(false); }}
                            />
                          )}
                          {notifications.agentRequests.length > 0 && (
                            <NotificationItem
                              icon={UserPlus}
                              color="#d4af37"
                              title={language === 'ar' ? 'طلبات عضوية وكيل' : 'Agent Requests'}
                              count={notifications.agentRequests.length}
                              onClick={() => { navigate('/manage/cloudgm-business?tab=agent-requests'); setShowNotifications(false); }}
                            />
                          )}
                          {notifications.serviceRequests.length > 0 && (
                            <NotificationItem
                              icon={FileText}
                              color="#d4af37"
                              title={language === 'ar' ? 'طلبات الخدمات' : 'Service Requests'}
                              count={notifications.serviceRequests.length}
                              onClick={() => { navigate('/manage/cloudgm?tab=services-categories'); setShowNotifications(false); }}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            <LanguageToggle className="!h-11 !rounded-xl" />
            
            {/* Home Button — navigates to main dashboard */}
            <Link to="/dashboard">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-11 h-11 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
                title={language === 'ar' ? 'الصفحة الرئيسية للوحة التحكم' : 'Main dashboard page'}
                data-testid="home-btn"
              >
                <Home className="h-5 w-5 text-[#A1A1AA]" />
              </motion.button>
            </Link>
            
            {/* Logout Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={logout}
              className="w-11 h-11 bg-red-500/10 rounded-xl flex items-center justify-center hover:bg-red-500/20 transition-all border border-red-500/20 hover:border-red-500/40"
              title={language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5 text-red-400" />
            </motion.button>
          </div>
        </div>
      </div>
    </header>
  );
}
